import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { depositAdminApiSlice } from '../../../api/deposit/depositAdminApiSlice';
import Logger from '../../../common/logger';
import { store } from '../../../store/store';
import { getApiErrorStatus } from '../../../utils/apiError';

// Shared submit/error handling for the deposit action modals (release and
// capture): run the mutation, close on success, otherwise map the contract's
// error statuses (spec §4) to localized messages — raw backend messages are
// English-only and would leak untranslated into the DE UI.
export const useDepositAction = (logName: string, onClose: () => void) => {
    const { t } = useTranslation();
    const [formError, setFormError] = useState<string | null>(null);

    const submit = async (action: () => Promise<unknown>) => {
        setFormError(null);
        try {
            await action();
            onClose();
        } catch (err) {
            Logger(logName).error(`Deposit action failed: ${JSON.stringify(err)}`);
            const status = getApiErrorStatus(err);
            // 409 = the precondition changed underneath us (auto-release or
            // another admin, spec §4). invalidatesTags only fires on
            // fulfilled mutations, so refetch explicitly — otherwise the row
            // keeps offering actions on an already-settled deposit.
            if (status === 409) {
                store.dispatch(depositAdminApiSlice.util.invalidateTags(['Deposits']));
            }
            setFormError(
                status === 409
                    ? t('deposits.errors.conflict')
                    : status === 403
                        ? t('deposits.errors.forbidden')
                        : t('deposits.errors.generic'),
            );
        }
    };

    return { formError, clearFormError: () => setFormError(null), submit };
};
